import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { finalize, Subscription } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { ImageService } from 'src/app/services/image.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { showFallbackHttpError } from 'src/app/shared/utils/http-error-toast.util';
import { ConstantUtil } from 'src/app/util/constant.util';

import { CityData } from './city-data';
import { IndustryData } from './industry-data';
import { AvatarUploadModalComponent } from '../avatar-modal/avatar-upload-modal.component';
import { ImageViewerComponent } from '../avatar-modal/image-viewer/image-viewer.component';

@Component({
  selector: 'app-tenant-basic-info-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzSelectModule,
    NzSpinModule,
    NzIconModule,
    NzGridModule,
    NzAvatarModule,
    NzDatePickerModule,
    NzTagModule,
    AvatarUploadModalComponent,
    ImageViewerComponent,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './tenant-basic-info-page.component.html',
  styleUrls: ['./tenant-basic-info-page.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TenantBasicInfoPageComponent implements OnInit, OnDestroy {
  private readonly http = inject(HttpService);
  private readonly imageService = inject(ImageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly message = inject(NzMessageService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  tenantId = '';
  loading = false;
  avatarUrl = '';
  tenantData: any = {};
  tenantForm!: FormGroup;
  imageViewerVisible = false;
  isAvatarModalVisible = false;
  isAvatarUploading = false;
  uploadData: any = { x: 0, y: 0, size: 80 };
  hasUnsavedChanges = false;
  private formSubscription: Subscription | null = null;
  private initialFormValueSnapshot = '';

  readonly industryOptions: any[] = IndustryData;
  readonly cityOptions: any[] = CityData;
  industrySubOptions: any[] = [];

  get tenantTypes() {
    return [
      { label: this.i18n.fanyi('tenant.detail.tenant-type.common'), value: 'Common' },
      { label: this.i18n.fanyi('tenant.detail.tenant-type.person'), value: 'Person' },
      { label: this.i18n.fanyi('tenant.detail.tenant-type.public'), value: 'Public' },
      { label: this.i18n.fanyi('tenant.detail.tenant-type.official'), value: 'Official' },
      { label: this.i18n.fanyi('tenant.detail.tenant-type.service'), value: 'Service' }
    ];
  }

  get scaleOptions() {
    return [
      { text: this.i18n.fanyi('tenant.detail.scale.mini'), value: 'Mini' },
      { text: this.i18n.fanyi('tenant.detail.scale.smart'), value: 'Smart' },
      { text: this.i18n.fanyi('tenant.detail.scale.middle'), value: 'Middle' },
      { text: this.i18n.fanyi('tenant.detail.scale.large'), value: 'Large' }
    ];
  }

  ngOnInit(): void {
    this.initForm();
    this.route.params.subscribe(params => {
      this.tenantId = params['tenantId'];
      this.loadTenantData();
    });
  }

  ngOnDestroy(): void {
    this.formSubscription?.unsubscribe();
  }

  initForm(): void {
    this.tenantForm = this.fb.group({
      name: [null, [Validators.required]],
      shortName: [null, [Validators.required]],
      code: [{ value: null, disabled: true }, [Validators.required]],
      type: [{ value: null, disabled: true }, [Validators.required]],
      industry: [null],
      industrySub: [null],
      scale: [null],
      city: [null],
      startTime: [null],
      endTime: [null, [this.endTimeValidator]],
      description: [null, [Validators.required]],
      address: [null],
      website: [null],
      unifiedNumber: [null],
      representativeNumber: [null],
      accountId: [{ value: null, disabled: true }],
      avatarId: [null]
    });
  }

  endTimeValidator = (control: any): { [key: string]: any } | null => {
    if (!control.value) {
      return null;
    }
    const startTime = this.tenantForm?.get('startTime')?.value;
    if (startTime && control.value < startTime) {
      return { endTimeInvalid: true };
    }
    return null;
  };

  onStartTimeChange(): void {
    this.tenantForm.get('endTime')?.updateValueAndValidity();
  }

  updateIndustrySubOptions(industry: string): void {
    if (!industry) {
      this.industrySubOptions = [];
      this.tenantForm.get('industrySub')?.setValue(null);
      return;
    }
    if (this.tenantData.industry !== industry) {
      this.tenantForm.get('industrySub')?.setValue(null);
    }
    this.industrySubOptions = this.industryOptions.find(item => item.value === industry)?.subItems || [];
    this.cdr.detectChanges();
  }

  loadTenantData(): void {
    this.loading = true;
    this.http
      .post(`${ConstantUtil.ADMIN_TENANT_ITEM}/${this.tenantId}`, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (!res.success) {
          this.message.error(this.i18n.fanyi('tenant.detail.load-tenant-info-failed'));
          return;
        }

        this.tenantData = res.data.tenantModel;
        this.patchFormValues();
      });
  }

  patchFormValues(): void {
    const startTime = this.tenantData.startTime ? new Date(this.tenantData.startTime) : null;
    const endTime = this.tenantData.endTime ? new Date(this.tenantData.endTime) : null;

    this.tenantForm.patchValue({
      name: this.tenantData.name,
      shortName: this.tenantData.shortName,
      code: this.tenantData.code,
      type: this.tenantData.type,
      industry: this.tenantData.industry,
      industrySub: this.tenantData.industrySub,
      scale: this.tenantData.scale,
      city: this.tenantData.city,
      startTime,
      endTime,
      description: this.tenantData.description,
      address: this.tenantData.address,
      website: this.tenantData.website,
      unifiedNumber: this.tenantData.unifiedNumber,
      representativeNumber: this.tenantData.representativeNumber,
      accountId: this.tenantData.accountId,
      avatarId: this.tenantData.avatarId
    });

    if (this.tenantData.industry) {
      this.updateIndustrySubOptions(this.tenantData.industry);
    }
    if (this.tenantData.avatarId) {
      this.loadAvatarImage(this.tenantData.avatarId);
    } else {
      this.avatarUrl = '';
    }

    this.formSubscription?.unsubscribe();
    this.formSubscription = this.tenantForm.valueChanges.subscribe(() => {
      this.hasUnsavedChanges = this.serializeFormValue(this.tenantForm.getRawValue()) !== this.initialFormValueSnapshot;
      this.cdr.markForCheck();
    });
    this.initialFormValueSnapshot = this.serializeFormValue(this.tenantForm.getRawValue());
    this.hasUnsavedChanges = false;
    this.cdr.detectChanges();
  }

  loadAvatarImage(avatarId: string): void {
    this.imageService.loadImage(avatarId, 'M').subscribe(imageUrl => {
      this.avatarUrl = imageUrl || '';
      this.cdr.detectChanges();
    });
  }

  getTenantTypeText(type: string): string {
    const textMap: { [key: string]: string } = {
      Common: this.i18n.fanyi('tenant.detail.tenant-type.common'),
      Person: this.i18n.fanyi('tenant.detail.tenant-type.person'),
      Public: this.i18n.fanyi('tenant.detail.tenant-type.public'),
      Official: this.i18n.fanyi('tenant.detail.tenant-type.official'),
      Service: this.i18n.fanyi('tenant.detail.tenant-type.service')
    };
    return textMap[type] || type;
  }

  saveTenant(): void {
    if (this.tenantForm.invalid) {
      Object.values(this.tenantForm.controls).forEach(control => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
      return;
    }

    const formData = this.tenantForm.getRawValue();
    const saveData = {
      id: this.tenantId,
      ...formData,
      industry: formData.industry ?? '',
      industrySub: formData.industrySub ?? '',
      scale: formData.scale ?? '',
      city: formData.city ?? '',
      website: this.normalizeOptionalUrl(formData.website),
      startTime: formData.startTime ? new Date(formData.startTime).getTime() : null,
      endTime: formData.endTime ? new Date(formData.endTime).getTime() : null
    };

    this.loading = true;
    this.http
      .post(ConstantUtil.ADMIN_TENANT_UPDATE, saveData)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          this.message.success(this.i18n.fanyi('tenant.detail.update-success'));
          this.loadTenantData();
        } else {
          if (!res.msg) {
            this.message.error(this.i18n.fanyi('tenant.detail.update-failed'));
          }
        }
      });
  }

  showAvatarUploadModal(): void {
    this.isAvatarModalVisible = true;
  }

  handleAvatarModalCancel(): void {
    this.isAvatarModalVisible = false;
  }

  handleAvatarCropped(event: { blob: Blob; width: number }): void {
    this.isAvatarUploading = true;
    const file = new File([event.blob], 'avatar.png', { type: 'image/png' });
    this.uploadData.id = this.tenantId;
    this.uploadData.size = event.width;

    this.http.postFormData(ConstantUtil.ADMIN_TENANT_AVATAR_SET, this.uploadData, file).subscribe({
      next: res => {
        if (res.success) {
          this.message.success(this.i18n.fanyi('tenant.detail.avatar-upload-success'));
          if (res.data) {
            this.tenantForm.patchValue({ avatarId: res.data });
            this.loadAvatarImage(res.data);
          }
        } else {
          showFallbackHttpError(this.message, res.msg, this.i18n.fanyi('tenant.detail.avatar-upload-failed'));
        }
        this.isAvatarUploading = false;
        this.isAvatarModalVisible = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.message.error(this.i18n.fanyi('tenant.detail.avatar-upload-failed'));
        this.isAvatarUploading = false;
        this.cdr.detectChanges();
      }
    });
  }

  openImageViewer(): void {
    if (!this.avatarUrl) {
      return;
    }
    this.imageViewerVisible = true;
  }

  closeImageViewer(): void {
    this.imageViewerVisible = false;
  }

  trackByValue(index: number, item: any) {
    return item?.value ?? item;
  }

  private serializeFormValue(value: any): string {
    return JSON.stringify(value, (_key, currentValue) => {
      if (currentValue instanceof Date) {
        return currentValue.getTime();
      }
      return currentValue;
    });
  }

  private normalizeOptionalUrl(value: string | null): string {
    const trimmedValue = value?.trim() ?? '';
    return trimmedValue ? trimmedValue : '';
  }
}
